import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, UserCheck, UserX, Zap, Circle, Car, Check } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";

export default function ExpressDriversPanel() {
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | express | pending
  const [saving, setSaving] = useState({});
  const [vehicleModal, setVehicleModal] = useState(null); // { driver, vehicles, loading, currentId }

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/drivers/express")
      .then(({ data }) => setDrivers(data.drivers || []))
      .catch(() => toast.error("Impossible de charger la liste des chauffeurs"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (driver, next) => {
    setSaving((prev) => ({ ...prev, [driver.id]: true }));
    try {
      await api.put(`/admin/drivers/${driver.id}/express-enable`, { enabled: next });
      setDrivers((prev) => prev.map((d) => (d.id === driver.id ? { ...d, express_enabled: next, is_online: next ? d.is_online : false } : d)));
      toast.success(next ? `${driver.name} ajouté à Trigo Express` : `${driver.name} retiré de Trigo Express`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving((prev) => ({ ...prev, [driver.id]: false }));
    }
  };

  const openVehicleModal = async (driver) => {
    setVehicleModal({ driver, vehicles: [], loading: true, currentId: driver.express_vehicle_id || "" });
    try {
      const { data } = await api.get(`/admin/drivers/${driver.id}/vehicles`);
      // Existing endpoint returns a raw list of vehicles.
      const vehicles = Array.isArray(data) ? data : (data.vehicles || []);
      setVehicleModal({ driver, vehicles, loading: false, currentId: driver.express_vehicle_id || "" });
    } catch (e) {
      toast.error("Impossible de charger les véhicules");
      setVehicleModal(null);
    }
  };

  const assignVehicle = async (vehicleId) => {
    if (!vehicleModal) return;
    const driver = vehicleModal.driver;
    try {
      await api.put(`/admin/drivers/${driver.id}/express-vehicle`, { vehicle_id: vehicleId });
      const chosen = vehicleModal.vehicles.find((v) => v.id === vehicleId);
      setDrivers((prev) => prev.map((d) => d.id === driver.id ? {
        ...d,
        express_vehicle_id: vehicleId,
        express_vehicle_brand: chosen?.brand || "",
        express_vehicle_model: chosen?.model || "",
        express_vehicle_plate: chosen?.plate_number || "",
      } : d));
      toast.success(vehicleId ? `Véhicule Express assigné à ${driver.name}` : `Véhicule Express retiré de ${driver.name}`);
      setVehicleModal(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const filtered = drivers.filter((d) => {
    if (search) {
      const s = search.toLowerCase();
      if (!(d.name || "").toLowerCase().includes(s) && !(d.phone || "").includes(s) && !(d.email || "").toLowerCase().includes(s)) return false;
    }
    if (filter === "express" && !d.express_enabled) return false;
    if (filter === "pending" && d.driver_status !== "approved") return false;
    return true;
  });

  const expressCount = drivers.filter((d) => d.express_enabled).length;
  const approvedCount = drivers.filter((d) => d.driver_status === "approved").length;

  return (
    <div className="space-y-4" data-testid="admin-express-drivers-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 mr-auto">
          <Zap className="w-5 h-5 text-[#FACC15]" />
          <h2 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>Chauffeurs Trigo Express</h2>
        </div>
        <span className="text-xs text-[#64748B]">
          <b className="text-[#0A2540]">{expressCount}</b> intégrés · <b className="text-[#0A2540]">{approvedCount}</b> approuvés · <b className="text-[#0A2540]">{drivers.length}</b> au total
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-[#94A3B8]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un chauffeur…" className="pl-9 h-9" data-testid="express-drivers-search" />
        </div>
        <div className="flex gap-1">
          {[
            { id: "all", label: "Tous" },
            { id: "express", label: "Trigo Express" },
            { id: "pending", label: "Approuvés" },
          ].map((f) => (
            <Button key={f.id} type="button" onClick={() => setFilter(f.id)} variant={filter === f.id ? "default" : "outline"} size="sm" className={filter === f.id ? "bg-[#0A2540] text-white" : ""} data-testid={`express-filter-${f.id}`}>
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#0A2540]" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#64748B]">Aucun chauffeur ne correspond aux filtres.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((d) => {
            const canEnroll = d.driver_status === "approved";
            const hasVehicle = !!d.express_vehicle_id;
            return (
              <div key={d.id} className="bg-white p-3 sm:p-4 rounded-xl border border-[#E2E8F0] flex flex-wrap items-center gap-3" data-testid={`express-driver-row-${d.id}`}>
                <div className="w-10 h-10 rounded-full bg-[#0A2540] text-white font-bold flex items-center justify-center shrink-0">
                  {(d.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#0A2540] text-sm" style={{ fontFamily: "Outfit" }}>{d.name}</span>
                    {d.driver_status === "approved" ? (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded flex items-center gap-1"><UserCheck className="w-3 h-3" /> Approuvé</span>
                    ) : (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded flex items-center gap-1"><UserX className="w-3 h-3" /> En attente</span>
                    )}
                    {d.express_enabled && d.is_online && (
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded flex items-center gap-1"><Circle className="w-2.5 h-2.5 fill-emerald-500 stroke-emerald-500" /> En ligne</span>
                    )}
                  </div>
                  <div className="text-xs text-[#64748B]">{d.phone || d.email}</div>
                  {d.express_enabled && (
                    <div className="text-[11px] mt-1 flex items-center gap-1">
                      <Car className="w-3 h-3 text-[#0A2540]" />
                      {hasVehicle ? (
                        <span className="text-emerald-700 font-semibold">
                          {d.express_vehicle_brand} {d.express_vehicle_model} · <span className="font-mono">{d.express_vehicle_plate}</span>
                        </span>
                      ) : (
                        <span className="text-amber-600 font-semibold">Aucun véhicule assigné</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {d.express_enabled && (
                    <Button size="sm" variant="outline" onClick={() => openVehicleModal(d)} className="border-[#CBD5E1] h-8" data-testid={`express-driver-assign-vehicle-${d.id}`}>
                      <Car className="w-3.5 h-3.5 mr-1" /> {hasVehicle ? "Changer véhicule" : "Assigner véhicule"}
                    </Button>
                  )}
                  <span className={`text-xs font-semibold ${d.express_enabled ? "text-emerald-700" : "text-[#94A3B8]"}`}>
                    {d.express_enabled ? "Trigo Express" : "Non intégré"}
                  </span>
                  <Switch
                    checked={!!d.express_enabled}
                    disabled={!canEnroll || !!saving[d.id]}
                    onCheckedChange={(v) => toggle(d, v)}
                    data-testid={`express-driver-toggle-${d.id}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Vehicle assignment modal */}
      <Dialog open={!!vehicleModal} onOpenChange={(o) => !o && setVehicleModal(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="assign-express-vehicle-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0A2540] flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Car className="w-5 h-5 text-[#FACC15]" /> Véhicule Trigo Express — {vehicleModal?.driver?.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">
              Sélectionnez le véhicule que ce chauffeur utilisera pour Trigo Express. Un seul véhicule peut être actif pour Express à la fois.
            </DialogDescription>
          </DialogHeader>
          {vehicleModal?.loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0A2540]" /></div>
          ) : (vehicleModal?.vehicles || []).length === 0 ? (
            <div className="py-8 text-center text-sm text-[#64748B]">
              Ce chauffeur n&apos;a aucun véhicule enregistré. Ajoutez d&apos;abord un véhicule dans la fiche chauffeur.
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {vehicleModal.vehicles.map((v) => {
                const active = v.id === vehicleModal.currentId;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => assignVehicle(v.id)}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${active ? "border-[#FACC15] bg-[#FEF3C7]" : "border-[#E2E8F0] hover:border-[#94A3B8] bg-white"}`}
                    data-testid={`express-vehicle-option-${v.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#F1F5F9] flex items-center justify-center shrink-0">
                        <Car className="w-5 h-5 text-[#0A2540]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#0A2540] text-sm">
                          {v.brand} {v.model} {v.year ? `· ${v.year}` : ""}
                        </div>
                        <div className="text-[11px] text-[#64748B]">
                          <span className="font-mono">{v.plate_number || "—"}</span>
                          {v.vehicle_type && ` · ${v.vehicle_type}`}
                          {v.seats ? ` · ${v.seats} places` : ""}
                          {v.color ? ` · ${v.color}` : ""}
                        </div>
                      </div>
                      {active && (
                        <span className="text-[10.5px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded flex items-center gap-1">
                          <Check className="w-3 h-3" /> Actif
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex justify-between gap-2 mt-2">
            {vehicleModal?.currentId && (
              <Button variant="outline" onClick={() => assignVehicle("")} className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10" data-testid="express-vehicle-unassign">
                Retirer l&apos;assignation
              </Button>
            )}
            <Button variant="outline" onClick={() => setVehicleModal(null)} className="ml-auto border-[#CBD5E1]" data-testid="express-vehicle-close">Fermer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
